<template>
  <div v-if="orderDetails">
    <div :class="{ 'container my-4': !isMobile }">
      <div class="order-details p-4">
        <div class="mb-3" v-if="prevRoute && prevRoute.path.includes('checkout')">
          <h1 class="pt-30 text-center">
            <img src="@/assets/images/big-check.svg" class="mr-10" alt />
            Thank you for ordering!
          </h1>
        </div>
        <div class="mb-3" v-else>
          <router-link v-if="loggedIn && orderDetails.orders.length" :to="previousRoute(orderDetails.orders[0].status)"
            class="back-link">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
          </router-link>
        </div>
        <div class="h5 mt-4 mb-5 fw-bold pb-3 border-bottom">
          Transaction ID {{ orderDetails.transaction_no }}
        </div>
        <b-container class="individual-order p-3 mt-4" v-for="(order, index) in orderDetails.orders"
          :key="'order' + index">
          <b-row class="main-details mb-4">
            <b-col md="6"> {{ order.brand_name }} ({{ order.branch }}) </b-col>
            <b-col md="6" class="text-md-right">
              &#8369; {{ order.order_item_total_amount | currency }}
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="6" md="2" class="details-label"> Order ID </b-col>
            <b-col cols="6" md="10">{{ order.order_no }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="6" md="2" class="details-label">Logistics Type</b-col>
            <b-col cols="6" md="10">
              <template v-if="['pickup', 'pick up'].includes(order.delivery_type.toLowerCase())">
                Pickup Now
              </template>
              <template v-else-if="order.delivery_type.toLowerCase() === 'delivery'">
                Deliver Now
              </template>
              <template v-else>
                {{ order.delivery_type }}
              </template>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="6" md="2" class="details-label">Order Status</b-col>
            <b-col cols="6" md="10">
              <span v-if="order.status" class="status text-capitalize" v-bind:class="order.status">
                <template v-if="!order.status"> Pending </template>
                <template v-else-if="order.status == 'in_process'">
                  Processing
                </template>
                <template v-else-if="order.status == 'payment_pending'">
                  Payment Pending
                </template>
                <template v-else-if="order.status == 'pick_up'">
                  Pickup
                </template>
                <template v-else>{{ order.status }}</template>
              </span>
              <span v-else class="status text-capitalize">
                Payment Pending
              </span>
            </b-col>
          </b-row>
          <ProofOfImageComponent :order="order" />

          <hr v-if="order.status" class="mt-4" />
          <router-link v-if="order.status" :to="`/${subdomain}/orders/${id}/${order.uuid}`" class="view-orders-link"
            :style="{ color: webDesign.design.main_color }">
            View Order Details
            <i class="bi bi-chevron-right" aria-hidden="true"></i>
          </router-link>
        </b-container>
        <b-container class="order-details-computations mt-3 pb-4 border-bottom">
          <b-row>
            <b-col offset-md="7"> Subtotal </b-col>
            <b-col class="text-right fw-bold">
              &#8369; {{ orderDetails.total | currency }}
            </b-col>
          </b-row>
          <b-row v-for="(fee, key) in orderDetails.fees" :key="key" class="mt-2">
            <b-col offset-md="7">{{ fee.name }}</b-col>
            <b-col class="text-right">
              <template v-if="fee.name === 'Delivery Fee'">
                <span 
                  v-if="get(orderDetails, 'voucher_info.is_free_delivery', false) || get(orderDetails, 'voucher_info.is_delivery_discount_enable', false)" 
                  :class="['fw-bold', { 'text-line-through': get(orderDetails, 'voucher_info.is_free_delivery', false) }]"
                >
                  &#8369; {{ fee.orig_value | currency}}
                </span>
                <span v-else class="fw-bold">
                  <template v-if="fee.calculated_amount">
                    &#8369; {{ fee.calculated_amount | currency }}
                  </template>
                  <template v-else>
                    FREE  
                  </template>
                </span>
              </template>  
              <span v-else class="fw-bold">
                &#8369; {{ fee.calculated_amount | currency }}   
              </span>
            </b-col>
          </b-row>
          <b-row v-if="loggedIn && hasMEDPadalaCustomization">
            <b-col offset-md="7" class="mt-2">
              <img :src="medPadalaCustomization.logo" alt="MEDPadala Credits" class="medpadala-logo" />
            </b-col>
            <b-col class="text-right fw-bold text-success">
              - &#8369; {{ medPadalaCustomizations.values.amount | price }}
            </b-col>
          </b-row>
          <template v-if="loggedIn && orderDetails.points_value">
            <b-row class="mt-2">
              <b-col offset-md="7" class="text-success">
                Points Redeemed
              </b-col>
              <b-col class="text-right fw-bold text-success">
                - &#8369; {{ orderDetails.points_value | currency }}
              </b-col>
            </b-row>
          </template>
          <template v-if="loggedIn && orderDetails.voucher_code">
            <b-row>
              <b-col offset-md="7" class="mt-2 text-success"> Promo </b-col>
            </b-row>
            <b-row>
              <b-col offset-md="7" class="text-success">
                <i class="bi bi-tag-fill text-success" aria-hidden="true"></i>
                {{ orderDetails.voucher_code }}
              </b-col>
              <b-col class="text-right fw-bold text-success">
                <template v-if="get(orderDetails, 'voucher_info.is_free_delivery', false)">
                  FREE DELIVERY
                </template>
                <template v-else>
                  - &#8369; {{ orderDetails.voucher_amount | currency }}
                </template>
              </b-col>
            </b-row>
          </template>
          <template v-if="loggedIn && isOrderApprovedSpecialDiscount && Boolean(specialDiscountTotal)">
            <b-row class="mt-2">
              <b-col offset-md="7" class="text-success">Special Discount</b-col>
              <b-col class="text-right fw-bold text-success">
                - &#8369; {{ specialDiscountTotal | currency }}
              </b-col>
            </b-row>
          </template>
        </b-container>
        <b-container>
          <b-row class="mt-4">
            <b-col offset-md="7" class="order-details-total fw-bold">
              Total Payment
            </b-col>
            <b-col class="text-right order-details-total fw-bold">
              &#8369; {{ orderDetails.computed_total | currency }}
            </b-col>
          </b-row>
          <b-row v-if="orderDetails.points_earned" class="mt-4">
            <b-col offset-md="7">
              <EarnedPoints :points="orderDetails.points_earned" :is-order-completed="isOrderCompleted" />
            </b-col>
          </b-row>
        </b-container>
      </div>
      <b-container class="bg-white p-4 mt-4">
        <b-row>
          <b-col md="6">
            <div class="h5 border-bottom pb-2">Payment Method</div>
            <div class="payment-method mt-3">
              <div v-if="orderDetails.payment_type === 'VOUCHER'">
                <i class="bi bi-tags text-primary" aria-hidden="true"></i>
                <h3 class="d-inline-block">VOUCHER</h3>
              </div>
              <div v-if="orderDetails.payment_type === 'POINTS'" class="d-flex align-items-center">
                <img src="@/assets/images/points-badge.svg" class="mr-2" alt="points">
                <h3 class="d-inline-block">Points</h3>
              </div>
              <div v-else>
                <img :src="paymentMethodDisplayIcon(orderDetails.payment_type)"
                  :alt="paymentMethodDisplayLabel(orderDetails.payment_type)" :style="{
                    width: !orderDetails.payment_type.includes('CC')
                      ? '60px'
                      : null,
                    'object-fit':
                      orderDetails.payment_type === 'SHOPEEPAY_XENDIT'
                        ? 'cover'
                        : null,
                    height: ['SHOPEEPAY_XENDIT', 'COD'].includes(
                      orderDetails.payment_type
                    )
                      ? '35px'
                      : null,
                  }" />
                <div class="d-inline-block ml-2">
                  {{ paymentMethodDisplayLabel(orderDetails.payment_type) }}
                  <span v-if="orderDetails.payment_type !== 'COD'">
                    <template v-if="orderDetails.payment_type.includes('PAYMONGO')">
                      via PayMongo
                    </template>
                    <template v-else-if="orderDetails.payment_type.includes('XENDIT')">
                      via Xendit
                    </template>
                    <template v-else-if="orderDetails.payment_type !== 'VOUCHER' && orderDetails.payment_type !== 'POINTS'"> via Paynamics </template>
                  </span>
                </div>
              </div>
            </div>
            <div class="mt-3">
              <div class="text-muted">Payment Status</div>
              <b-badge class="font-weight-normal mt-1 p-2 text-white"
                :variant="isPaymentSuccessful ? 'gradient-success' : 'gradient-secondary'" style="font-size: 16px">
                {{ isPaymentSuccessful ? "Payment Successful" : "Payment Pending" }}
              </b-badge>
              <small v-if="!isPaymentSuccessful" class="d-block mt-1" style="color: #5182ff">
                Please wait while we confirm your payment
              </small>
            </div>
          </b-col>
          <b-col md="6" class="header mt-3 mt-md-0" v-if="orderDetails.billing_details">
            <div class="h5 border-bottom pb-2">Billing Details</div>
            <div class="mt-3">
              {{ orderDetails.billing_details.firstname }}
              {{ orderDetails.billing_details.lastname }}
            </div>
            <div>{{ orderDetails.billing_details.mobile_number }}</div>
            <div>{{ orderDetails.billing_details.email }}</div>
            <div class="mt-3" v-if="orderDetails.billing_details.address_line1">
              {{ orderDetails.billing_details.address_line1 }},
              {{ orderDetails.billing_details.address_line2 }},
            </div>
            <div v-if="orderDetails.billing_details.address_line1">
              {{ orderDetails.billing_details.city }},
              {{ orderDetails.billing_details.postal_code }},
              {{ orderDetails.billing_details.state }}, Philippines
            </div>
          </b-col>
        </b-row>
        <div v-if="orderDetails.payment_type == 'BT' && storeDetails" class="mt-4">
          <div class="h5">Bank Deposit Instructions</div>
          <div class="bank-deposit-instructions" v-for="(bankDetail, index) in storeDetails.bank_accounts" :key="index">
            <p v-if="webDesign !== null && webDesign.design !== null" :style="{ color: webDesign.design.main_color }"
              style="cursor: pointer" @click="showBankModal(bankDetail.uuid)">
              For {{ bankDetail.bank }} Bank Deposit
            </p>
            <p v-else style="cursor: pointer" @click="showBankModal(bankDetail.uuid)">
              For {{ bankDetail.bank }} Bank Deposit
            </p>
          </div>
        </div>
        <div class="mt-4" v-if="orderDetails.payment_type == 'BT' && loggedIn">
          <div class="h5">Upload Deposit Slip</div>
          <b-form @submit="uploadCustomerDepositSlip">
            <div class="update-deposit-slip">
              <b-row>
                <b-col md="6">
                  <b-form-group id="bank-fieldset" label="Bank" label-for="bank">
                    <b-form-select id="bank" v-model="form.selectedBank" :options="bankOptions"></b-form-select>
                  </b-form-group>
                  <b-form-group id="reference-number-fieldset" label="Reference Number" label-for="reference-number">
                    <b-form-input id="reference-number" v-model="form.reference_number"></b-form-input>
                  </b-form-group>
                  <b-form-group id="file-upload-fieldset" label="File Upload" label-for="file-upload"
                    class="file-upload-container">
                    <b-form-file v-model="form.depositSlip" accept="image/jpeg, image/png, image/gif"
                      placeholder="Drop your file here, or browse" drop-placeholder="Drop your file here..."
                      ref="file-input"></b-form-file>
                  </b-form-group>
                  <div class="invalid-feedback d-block mb-2">
                    {{ invalidFeedback("file_upload") }}
                  </div>
                  <b-button class="py-2" type="submit" variant="primary" block
                    :disabled="!form.depositSlip || authIsLoading || merchantIsLoading || estoreIsLoading"
                    v-if="webDesign !== null && webDesign.design !== null" :style="{
                      backgroundColor:
                        webDesign.design.main_color + '!important',
                      border: 'none !important',
                      color: webDesign.design.button_text_color + '!important',
                    }">
                    UPLOAD
                  </b-button>
                  <b-button class="py-2" type="submit" variant="primary" block
                    :disabled="!form.depositSlip || authIsLoading || merchantIsLoading || estoreIsLoading" v-else>
                    UPLOAD
                  </b-button>
                </b-col>
                <b-col class="pl-3 pl-md-5 mt-3 mt-md-0" md="6" v-if="orderDetails.deposit_slips">
                  <b-form-group id="file-uploaded-fieldset" label="File Uploaded" label-for="file-uploaded"
                    v-if="orderDetails.deposit_slips.length > 0">
                    <div class="file-uploaded-container p-3">
                      <b-row no-gutters>
                        <b-col>
                          <img src="@/assets/images/attach.svg" class="mr-2" alt />
                          <p class="file-name d-inline-block">
                            {{
                              orderDetails.deposit_slips[
                                orderDetails.deposit_slips.length - 1
                              ].image_name.substr(
                                orderDetails.deposit_slips[
                                  orderDetails.deposit_slips.length - 1
                                ].image_name.lastIndexOf("/") + 1
                              )
                            }}
                          </p>
                        </b-col>
                        <b-col cols="3">
                          <span class="status text-capitalize float-right"
                            v-bind:class="orderDetails.deposit_slips[orderDetails.deposit_slips.length - 1].status">
                            <template v-if="orderDetails.deposit_slips[
                              orderDetails.deposit_slips.length - 1
                            ].status == 'pending'
                            ">
                              For Approval
                            </template>
                            <template else>{{
                              orderDetails.deposit_slips[
                                orderDetails.deposit_slips.length - 1
                              ].status
                            }}
                            </template>
                          </span>
                        </b-col>
                      </b-row>
                      <b-row no-gutters class="mt-2">
                        <b-col>
                          <p class="bank-reference-number mb-1">
                            Bank -
                            <span>
                              {{
                                orderDetails.deposit_slips[
                                  orderDetails.deposit_slips.length - 1
                                ].bank_details.bank
                              }}
                            </span>
                          </p>
                        </b-col>
                        <b-col>
                          <p class="bank-reference-number mb-1 text-right">
                            Reference Number -
                            <span>
                              {{
                                orderDetails.deposit_slips[
                                  orderDetails.deposit_slips.length - 1
                                ].reference_no
                              }}
                            </span>
                          </p>
                        </b-col>
                      </b-row>
                      <b-row no-gutters
                        v-if="orderDetails.deposit_slips[orderDetails.deposit_slips.length - 1].status == 'rejected'">
                        <b-col>
                          <p class="note mt-2 mb-1">
                            Note:
                            {{ orderDetails.deposit_slips[orderDetails.deposit_slips.length - 1].remarks }}
                          </p>
                        </b-col>
                      </b-row>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </b-form>
        </div>
      </b-container>
    </div>
    <b-modal ref="bankModal" hide-footer hide-header>
      <div class="modal-header">
        <button type="button" @click="hideBankModal" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="p-5" v-if="specificBank">
        <h3 class="mb-4">{{ specificBank[0].bank }} Bank Details</h3>
        <label for="account-name">Account Name</label>
        <p class="mb-4">
          <strong>{{ specificBank[0].account_name }}</strong>
        </p>
        <label for="account-number">Account Number</label>
        <p>
          <strong>{{ specificBank[0].account_no }}</strong>
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import $ from "jquery";
import {
  paymentMethodDisplayIcon,
  paymentMethodDisplayLabel,
} from "@/utilities";
import ProofOfImageComponent from "./ProofOfImageComponent.vue";
import EarnedPoints from "./EarnedPoints.vue";
import { get } from "lodash";

export default {
  data() {
    return {
      get,
      form: {
        depositSlip: null,
        reference_number: "",
        selectedBank: null,
      },
      prevRoute: null,
      specificBank: null,
      paymentMethodDisplayIcon,
      paymentMethodDisplayLabel,
    };
  },
  beforeRouteEnter(_, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  components: { ProofOfImageComponent, EarnedPoints },
  computed: {
    ...mapState("auth", {
      token: (state) => state.token,
      authIsLoading: (state) => state.loading,
    }),
    ...mapState("merchant", {
      subdomain: (state) => state.subdomain,
      customerPortalToken: (state) => state.customerPortalToken,
      merchantIsLoading: (state) => state.loading
    }),
    ...mapState("estore", {
      estoreIsLoading: (state) => state.loading,
      estoreErrors: (state) => state.errors,
      webDesign: (state) => state.webDesign,
      storeDetails: (state) => state.storeDetails,
    }),
    ...mapGetters({
      loggedIn: "auth/loggedIn",
      orderDetails: "estore/orderDetails",
      bankOptions: "estore/bankOptions",
      medPadalaCustomization: "medpadala/medPadalaCustomization",
      specialDiscountTotal: "estore/specialDiscountTotal",
      isOrderRejectedSpecialDiscount: "estore/isOrderRejectedSpecialDiscount",
      isOrderApprovedSpecialDiscount: "estore/isOrderApprovedSpecialDiscount",
      isOrderCompleted: "estore/isOrderCompleted",
      isMobile: "isMobile/isMobile"
    }),
    hasMEDPadalaCustomization() {
      if (!this.orderDetails) return false;

      return this.orderDetails.customizations
        .map((customization) => customization.type)
        .includes("med-padala");
    },
    medPadalaCustomizations() {
      if (!this.orderDetails) return null;

      return this.orderDetails.customizations.find(
        (customization) => customization.type === "med-padala"
      );
    },
    isPaymentSuccessful() {
      if (!this.orderDetails) return false;

      if (this.orderDetails.payment_type === "COD") {
        return this.orderDetails.orders.some(
          (order) =>
            order.status && ["delivered", "claimed"].includes(order.status)
        );
      }

      return this.orderDetails.orders.some(
        (order) => order.status && order.status !== "payment_pending" && !(order.status === "cancelled" && order.special_discount_approved == 0)
      );
    },
  },
  methods: {
    showBankModal(uuid) {
      if (this.storeDetails.bank_accounts.length > 0) {
        this.specificBank = this.storeDetails.bank_accounts.filter((bank) => {
          if (bank.uuid == uuid) {
            return bank;
          }
        });
      }
      this.$refs["bankModal"].show();
    },
    hideBankModal() {
      this.$refs["bankModal"].hide();
    },
    async getOrderDetails() {
      if (this.customerPortalToken) {
        const header = {
          "X-Merchant-Token": this.customerPortalToken,
        };

        if (this.token) header["Authorization"] = this.token;

        await this.$store.dispatch("estore/getOrderDetails", {
          header,
          id: this.id,
        });
      }
    },
    async uploadCustomerDepositSlip(evt) {
      evt.preventDefault();
      this.$store.dispatch("customer/resetErrors");
      this.$store.dispatch("estore/resetErrors");
      let depositSlip = this.form.depositSlip
        ? await this.getBase64(this.form.depositSlip)
        : null;
      depositSlip = depositSlip.replace(/data:image\/png;base64,/gi, "");
      depositSlip = depositSlip.replace(/data:image\/jpg;base64,/gi, "");
      depositSlip = depositSlip.replace(/data:image\/jpeg;base64,/gi, "");
      depositSlip = depositSlip.replace(/data:image\/gif;base64,/gi, "");

      let payload = {
        transaction_no: this.orderDetails.transaction_no,
        bank_uuid: this.form.selectedBank,
        deposit_slip: depositSlip,
        reference_no: this.form.reference_number,
      };

      let request = {
        header: {
          Authorization: this.token,
          "X-Merchant-Token": this.customerPortalToken,
        },
        payload: payload,
      };
      let _this = this;
      await this.$store
        .dispatch("estore/uploadCustomerDepositSlip", request)
        .then(() => {
          if (!_this.estoreErrors) {
            _this.getOrderDetails();
            _this.$refs["file-input"].reset();
            _this.depositSlip = null;
            _this.reference_number = "";
            _this.selectedBank = null;
            _this.$bvToast.toast("Successfully uploaded deposit slip.", {
              title: "Notification",
              autoHideDelay: 5000,
              variant: "success",
              appendToast: true,
            });
          }
        });
    },
    async getBase64(file) {
      return new Promise(function (resolve, reject) {
        const reader = new FileReader();
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    previousRoute(status) {
      const previousRoute = `/${this.subdomain}/orders`;

      if (["delivered", "claimed", "cancelled"].includes(status)) {
        return `${previousRoute}/past`;
      }
      return `${previousRoute}/upcoming`;
    },
    invalidFeedback(field) {
      if (this.estoreErrors) return this.estoreErrors.errors[field];

      return "";
    }
  },
  async created() {
    await this.getOrderDetails();
    this.$root.$emit('refreshNavValues', ['viewCart', 'getCustomerBalance']);
  },
  filters: {
    currency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "decimal",
        minimumFractionDigits: 2,
      }).format(Number.parseFloat(value).toFixed(2));
    },
  },
  mounted() {
    $("body").css({ backgroundColor: "#f4f5f8" });
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/views/orders/_order_details.scss";
</style>
